import App from '@/App';
import { matchDrillDownPath, STACK_SEPARATOR } from '@/app/navigation';
import {
  AllStackPanelsState,
  AllStacksState,
  deserializePanelStateFromSearchParams,
  PanelLocation,
  PanelState,
  serializePanelStateToSearchParams,
} from '@/app/navigation/panel-state-utils';
import { drillDownTree } from '@/app/routes';
import { LoginPage } from '@/components/auth/LoginPage';
import {
  ActivatePage,
  ConfirmEmailPage,
  ForgotPasswordPage,
  RecoverPasswordPage,
} from '@/features/account-access';

import { qrLocationPrevUrl } from '@/config/prevOrigin';
import { OfflineManager } from '@/features/offline/OfflineManager';
import { createUsableSingleton } from '@/hooks/createUsableSingleton';
import { sanitizeRedirect } from '@/lib/auth-redirect';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { reverse } from 'remeda';

function useInnerAppRouterState() {
  const location = useLocation();
  const search = useSearch({ strict: false });
  const paths = useMemo(() => {
    // Split at the first occurrence of '/~' (STACK_SEPARATOR)
    return location.pathname.split(STACK_SEPARATOR);
  }, [location.pathname]);
  const [stackKeys, setStackKeys] = React.useState(() => [
    Math.random().toString(36).slice(2),
    Math.random().toString(36).slice(2),
  ]);
  const panelState: AllStacksState = useMemo(() => {
    return deserializePanelStateFromSearchParams(search)[0];
  }, [search]);

  const drillDownPaths = useMemo(() => {
    return paths.map((segments, idx) => {
      if (!segments) {
        return null;
      }
      const drillDownPath = matchDrillDownPath(
        drillDownTree,
        segments.split('/').filter(Boolean),
      );
      return { drillDownPath, key: stackKeys[idx], stackIdx: idx };
    });
  }, [paths, stackKeys]);

  return {
    location,
    search,
    paths,
    drillDownPaths,
    stackKeys,
    panelState,
    setStackKeys,
  };
}
export const [AppRouterStateProvider, useAppRouterState] =
  createUsableSingleton(useInnerAppRouterState);

// Create the context, type inferred from router instance
export const AppRouterInterfaceContext = React.createContext<ReturnType<
  typeof useAppRouterItf
> | null>(null);

function useAppRouterItf() {
  /** atomically modify panel state for all panels whilst ignoring auxilary search params  */
  const modifyPanelState = (
    func: (panelState: AllStacksState) => AllStacksState,
  ) => {
    return (prev: AllStackPanelsState) => {
      const [panelState, rest] = deserializePanelStateFromSearchParams(prev);
      const newPanelState = func(panelState);
      return {
        ...rest,
        ...serializePanelStateToSearchParams(newPanelState),
      };
    };
  };

  // track router state in a ref to avoid rerenders by allowing the routerInterface to be a stable object
  const routerState = useAppRouterState();
  const routerStateRef = useRef(routerState);
  const navigate = useNavigate();
  useEffect(() => {
    routerStateRef.current = routerState;
  }, [routerState]);

  // FIXME: panelState changes causes a lot of rerenders because of this big interface
  return useMemo(
    () => ({
      swapStacks() {
        const { paths, stackKeys, setStackKeys } = routerStateRef.current;
        setStackKeys(reverse(stackKeys));
        const [primary, secondary] = paths;
        const newUrl = [secondary, primary].join(STACK_SEPARATOR);

        navigate({
          to: newUrl,
          search: modifyPanelState((panelState) => {
            return {
              0: panelState[1],
              1: panelState[0],
            };
          }),
        });
      },
      copyStack(from: number, to: number) {
        const { paths } = routerStateRef.current;
        const newPaths = [...paths];
        newPaths[to] = paths[from];
        const newUrl = newPaths.join(STACK_SEPARATOR);
        navigate({
          to: newUrl,
          search: modifyPanelState((panelState) => {
            panelState[to] = panelState[from];
            return panelState;
          }),
        });
      },
      emptyStack(idx: number) {
        const { paths } = routerStateRef.current;
        const newPaths = [...paths];
        newPaths[idx] = '/';
        const newUrl = newPaths.join(STACK_SEPARATOR);

        // Remove all search params for this stack
        navigate({
          to: newUrl,
          search: modifyPanelState((panelState) => {
            delete panelState[idx];
            return panelState;
          }),
        });
      },
      dropStack(idx: number) {
        const { paths } = routerStateRef.current;
        const newPaths = [...paths];
        newPaths.splice(idx, 1);
        const newUrl = newPaths.join(STACK_SEPARATOR);

        navigate({
          to: newUrl,
          search: modifyPanelState((panelState) => {
            delete panelState[idx];
            return panelState;
          }),
        });
      },
      getNavigationUrlForStack(idx: number, to: string) {
        const { paths } = routerStateRef.current;
        // TODO: allow passing in panel state
        const newPaths = [...paths];
        newPaths[idx] = to;
        return newPaths.join(STACK_SEPARATOR);
      },
      getPanelState({ stack, panel }: PanelLocation) {
        const { panelState } = routerStateRef.current;
        return panelState[stack]?.[panel] || {};
      },
      setPanelState(
        { stack, panel }: PanelLocation,
        stateOrFunc: PanelState | ((prev: PanelState) => PanelState),
      ) {
        const func =
          typeof stateOrFunc === 'function'
            ? stateOrFunc
            : (prev: PanelState) => ({ ...prev, ...stateOrFunc });
        navigate({
          to: '.',
          from: routerStateRef.current.location.pathname,
          search: modifyPanelState((panelState) => {
            if (!panelState[stack]) {
              panelState[stack] = { [panel]: {} };
            }
            panelState[stack][panel] = func(panelState[stack]?.[panel] || {});
            return panelState;
          }),
          replace: true,
        });
      },
      clearPanelState({ stack, panel }: PanelLocation) {
        navigate({
          from: '.',
          search: modifyPanelState((panelState) => {
            delete panelState[stack]?.[panel];
            return panelState;
          }),
          replace: true,
        });
      },
      clearStackState(stack: number) {
        navigate({
          from: '.',
          search: modifyPanelState((panelState) => {
            delete panelState[stack];
            return panelState;
          }),
          replace: true,
        });
      },
    }),
    [navigate],
  );
}

export function useAppRouterInterface() {
  const ctx = useContext(AppRouterInterfaceContext);
  if (!ctx)
    throw new Error(
      'useAppRouter must be used within AppRouterContext.Provider',
    );
  return ctx;
}

// FIXME: This is a mess, refactor
/** Cant render AppRouterStateProvider without an additional wrapper */
function InnerWrapper() {
  return (
    <AppRouterInterfaceContext.Provider value={useAppRouterItf()}>
      <App />
    </AppRouterInterfaceContext.Provider>
  );
}

function AppWrapper() {
  useAuthGuard();
  return (
    <AppRouterStateProvider>
      <InnerWrapper />
    </AppRouterStateProvider>
  );
}

// Always-mounted layout. OfflineManager lives here (not in main.tsx) because
// its release-notes dialog renders router <Link>s, which need RouterProvider.
function RootLayout() {
  return (
    <>
      <OfflineManager />
      <Outlet />
    </>
  );
}

// Create router once at module level (stable across HMR)
const rootRoute = createRootRoute({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: sanitizeRedirect(search.redirect),
  }),
  component: LoginPage,
});

// Landing pages of the account mails. The token travels in the query string.
const tokenSearch = (search: Record<string, unknown>) => ({
  token: typeof search.token === 'string' ? search.token : undefined,
});

const activateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate',
  validateSearch: tokenSearch,
  component: ActivatePage,
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot',
  component: ForgotPasswordPage,
});

const recoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recover',
  validateSearch: tokenSearch,
  component: RecoverPasswordPage,
});

const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-email',
  validateSearch: tokenSearch,
  component: ConfirmEmailPage,
});

/**
 * Scanned location labels (`/qr/location/<uuid>`) belong to prev, where
 * inventory lives. nginx forwards them on the canonical host, but a device
 * with the offline service worker installed never reaches nginx for a
 * navigation: the worker serves the app shell from cache and the router sees
 * the QR path. Prev is another origin, so this is a full navigation.
 */
export function redirectQrLocation(location: {
  pathname: string;
  searchStr: string;
}): void {
  const prevUrl = qrLocationPrevUrl(location.pathname, location.searchStr);
  if (prevUrl) {
    window.location.replace(prevUrl);
  }
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/*',
  beforeLoad: ({ location }) => redirectQrLocation(location),
  component: AppWrapper,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  activateRoute,
  forgotRoute,
  recoverRoute,
  confirmEmailRoute,
  appRoute,
]);

export const router = createRouter({
  routeTree,
});

export function Root() {
  return <RouterProvider router={router} />;
}
